import RMTable from 'components/customComponents/Controls/RMTable';
import React, { useMemo, useEffect, useState, useContext } from 'react';
import { ReactComponent as DeleteIcon } from 'Icons/CommandPanel/Delete_Dark.svg';
import { isNullOrUndefined } from 'utils/Global';
import ScreenWizardContext from './ScreenWizardContext';
import { PlateTypeDTO, ProteinInfoDTO } from 'business/models/Models';
import TableInput from './TableInput';
import LayersError from 'components/customComponents/Layers/LayersError';
import DropUpDown from 'components/customComponents/Controls/DropUpDown';
import './ScreenExperimentWizard.scss';

type ProteinRowModel = {
  name: string;
  dropNumber: any;
  dropVolume: any;
  solutionVolume: any;
  delete: any;
};

type DropVolumeError = {
  dropIndex: number;
  name: string;
};

type VolumeField = 'dropVolume' | 'proteinVolume';

function SelectedProteins({ item = null }: any) {
  const screenExpWiz = useContext(ScreenWizardContext);
  const plateType: PlateTypeDTO = screenExpWiz.plateType;
  const lProteins = screenExpWiz.proteins;
  const dropCount = plateType?.enabledDropNumbers?.length ?? 0;
  const maxDropVolume = plateType?.dropVolume ?? 0;
  const [proteinsError, setProteinsError] = useState<DropVolumeError[]>([]);
  const [data, setData] = useState<ProteinRowModel[]>([]);

  function isEmptyDrop(protein: ProteinInfoDTO | null) {
    return isNullOrUndefined(protein) || isNullOrUndefined(protein.name);
  }

  function getDropSlots(proteins: (ProteinInfoDTO | null)[]) {
    const drops: (ProteinInfoDTO | null)[] = [];
    for (let index = 0; index < dropCount; index++) {
      drops.push(isNullOrUndefined(proteins[index]) ? null : proteins[index]);
    }
    return drops;
  }

  useEffect(() => {
    if (isNullOrUndefined(item) || item.length === 0 || dropCount === 0) {
      return;
    }
    const drops = getDropSlots(lProteins);
    const pendingProteins = item.map((protein) => {
      return { ...protein };
    });
    for (let index = 0; index < drops.length && pendingProteins.length > 0; index++) {
      if (isEmptyDrop(drops[index])) {
        drops[index] = pendingProteins.shift();
      }
    }
    screenExpWiz.changeProteins(drops);
  }, [item]);

  useEffect(() => {
    screenExpWiz.setProteinVolError(proteinsError.length > 0);
  }, [proteinsError]);

  function getEmptyProtein(dropIndex: number): ProteinRowModel {
    return {
      name: null,
      dropNumber: (
        <DropUpDown
          val={dropIndex + 1}
          onDownClick={() => {}}
          onUpClick={() => {}}
          upDisabled={true}
          downDisabled={true}
        />
      ),
      dropVolume: null,
      solutionVolume: null,
      delete: (
        <DeleteIcon
          id="selectedprotein-del-icon"
          onClick={() => {}}
          className="cursor-pointer grey-svg"
        />
      ),
    };
  }

  function getErrorsView(errors: DropVolumeError[]) {
    return errors.map((error) => (
      <LayersError
        key={`dropVolError${error.dropIndex}`}
        className="p-[10px]"
        errorText={`Drop ${error.dropIndex + 1} (${
          error.name
        })-Drop Vol and Solution Vol must total a value in range (0 - ${maxDropVolume})`}
      />
    ));
  }

  function isDropVolumeValid(dropVolume: number, solutionVolume: number) {
    if (!Number.isFinite(dropVolume) || !Number.isFinite(solutionVolume)) {
      return false;
    }
    if (dropVolume < 0 || solutionVolume < 0) {
      return false;
    }
    return dropVolume + solutionVolume <= maxDropVolume;
  }

  function getVolumeErrors(proteins: (ProteinInfoDTO | null)[]) {
    const errors: DropVolumeError[] = [];
    getDropSlots(proteins).forEach((protein, dropIndex) => {
      if (isEmptyDrop(protein)) {
        return;
      }
      if (!isDropVolumeValid(protein.dropVolume, protein.proteinVolume)) {
        errors.push({ dropIndex, name: protein.name });
      }
    });
    return errors;
  }

  function getVolumeText(volume: number) {
    return Number.isFinite(volume) ? volume.toString() : '';
  }

  function updateVolume(
    dropIndex: number,
    protein: ProteinInfoDTO,
    field: VolumeField,
    value: string,
  ) {
    const parsedValue = parseFloat(value);
    if (!Number.isNaN(parsedValue)) {
      protein[field] = parsedValue;
    }
    const dropVolume = field === 'dropVolume' ? parsedValue : protein.dropVolume;
    const solutionVolume =
      field === 'proteinVolume' ? parsedValue : protein.proteinVolume;
    const isValid = isDropVolumeValid(dropVolume, solutionVolume);
    setProteinsError((prevErrors) => {
      const hasError = prevErrors.some((error) => error.dropIndex === dropIndex);
      if (isValid) {
        return hasError
          ? prevErrors.filter((error) => error.dropIndex !== dropIndex)
          : prevErrors;
      }
      return hasError ? prevErrors : [...prevErrors, { dropIndex, name: protein.name }];
    });
  }

  function moveProtein(fromIndex: number, toIndex: number) {
    if (toIndex < 0 || toIndex >= dropCount) {
      return;
    }
    const drops = getDropSlots(lProteins);
    const movedProtein = drops[fromIndex];
    if (isEmptyDrop(movedProtein)) {
      return;
    }
    drops[fromIndex] = drops[toIndex];
    drops[toIndex] = movedProtein;
    screenExpWiz.changeProteins(drops);
  }

  function getProteinRows(proteins: (ProteinInfoDTO | null)[]) {
    return getDropSlots(proteins).map((protein, index) => {
      if (isEmptyDrop(protein)) {
        return getEmptyProtein(index);
      }
      return {
        name: protein.name,
        dropNumber: (
          <DropUpDown
            val={index + 1}
            onDownClick={() => moveProtein(index, index + 1)}
            onUpClick={() => moveProtein(index, index - 1)}
            upDisabled={index === 0}
            downDisabled={index === dropCount - 1}
          />
        ),
        dropVolume: (
          <TableInput
            initialValue={getVolumeText(protein.dropVolume)}
            changeInputValue={(value) =>
              updateVolume(index, protein, 'dropVolume', value)
            }
            className="w-[75%] max-w-[75%]"
            step={0.1}
          />
        ),
        solutionVolume: (
          <TableInput
            initialValue={getVolumeText(protein.proteinVolume)}
            changeInputValue={(value) =>
              updateVolume(index, protein, 'proteinVolume', value)
            }
            className="w-[75%] max-w-[75%]"
            step={0.1}
          />
        ),
        delete: (
          <DeleteIcon
            id="selectedprotein-del-protein-icon"
            onClick={() => deleteProtein(index)}
            className="cursor-pointer"
          />
        ),
      };
    });
  }

  useEffect(() => {
    setData(getProteinRows(lProteins));
    setProteinsError(getVolumeErrors(lProteins));
  }, [lProteins, plateType]);

  function deleteAllProteins() {
    screenExpWiz.changeProteins([]);
  }
  function deleteProtein(delIndex) {
    const drops = getDropSlots(lProteins);
    drops[delIndex] = null;
    screenExpWiz.changeProteins(drops);
  }
  const columns = useMemo(
    () => [
      {
        Header: 'Protein Name',
        accessor: 'name',
        className: 'rmtable-custom-style1 col-6 overflow-hidden text-ellipsis',
      },
      {
        Header: 'Drop',
        accessor: 'dropNumber',
        className: 'col-2',
      },
      {
        Header: 'Drop Vol(µL)',
        accessor: 'dropVolume',
        className: 'col-2',
      },
      {
        Header: 'Solution Vol(µL)',
        accessor: 'solutionVolume',
        className: 'col-2',
      },
      {
        Header: (
          <DeleteIcon
            id="selectedprotein-del-all"
            onClick={deleteAllProteins}
            className="cursor-pointer"
          />
        ),
        accessor: 'delete',
        className: 'col-2',
      },
    ],
    [],
  );

  return (
    <>
      <div
        id="selectedprotein-table-container"
        style={{ height: '80%', overflow: 'auto' }}
      >
        <RMTable
          columns={columns}
          data={data}
          mainContainerStyle={{ height: '100%' }}
          showToolTipOnHover={true}
        />
      </div>
      <div
        id="selectedprotein-error-container"
        style={{ height: '20%', overflow: 'auto' }}
      >
        {getErrorsView(proteinsError)}
      </div>
    </>
  );
}

export default SelectedProteins;
